// Slice 7 — Predictive Dialer Console
// Live control panel: per-campaign start/pause/stop + KPIs + pacing simulator.
import { useEffect, useMemo, useState } from "react";
import { Play, Pause, Square, Activity, Rocket, Loader2, Gauge, Phone, AlertTriangle, Lightbulb, TrendingUp, TrendingDown, Minus, Stethoscope, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CallingWindowPill } from "@/admin/components/CallingWindowEditor";

const MODE_BADGE = {
  preview:     { label: "Preview",     color: "bg-blue-100 text-blue-900 border-blue-300" },
  progressive: { label: "Progressive", color: "bg-amber-100 text-amber-900 border-amber-300" },
  power:       { label: "Power",       color: "bg-violet-100 text-violet-900 border-violet-300" },
  predictive:  { label: "Predictive",  color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
};

const STATE_BADGE = {
  running: { label: "Running", color: "bg-emerald-600 text-white" },
  paused:  { label: "Paused",  color: "bg-amber-500 text-white" },
  stopped: { label: "Stopped", color: "bg-slate-500 text-white" },
};

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function ratio(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

// -------------------------------------------------------------
//  Daily Insights — "what would have happened" reflection
// -------------------------------------------------------------
const SEVERITY_STYLE = {
  good:    { bg: "bg-emerald-50 border-emerald-200 text-emerald-900", dotBg: "bg-emerald-500", icon: TrendingUp },
  bad:     { bg: "bg-red-50 border-red-200 text-red-900",             dotBg: "bg-red-500",     icon: TrendingDown },
  warn:    { bg: "bg-amber-50 border-amber-200 text-amber-900",       dotBg: "bg-amber-500",   icon: TrendingDown },
  neutral: { bg: "bg-slate-50 border-slate-200 text-slate-800",       dotBg: "bg-slate-400",   icon: Minus },
};

function InsightItem({ insight }) {
  const cfg = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.neutral;
  const Icon = cfg.icon;
  const s = insight.stats || {};
  return (
    <div
      data-testid={`insight-${insight.campaign_id}`}
      className={`relative rounded-lg border p-4 ${cfg.bg}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-7 h-7 rounded-full ${cfg.dotBg} flex items-center justify-center shrink-0`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug" data-testid={`insight-headline-${insight.campaign_id}`}>
            {insight.headline}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3 text-[11px]">
            <Stat label="Dials" value={s.total_dials} />
            <Stat label="Answered" value={s.answered} />
            <Stat label="Abandon" value={`${s.actual_abandon_pct?.toFixed(1)}%`} />
            <Stat label="Now → suggested" value={`x${s.actual_multiplier} → x${s.recommended_multiplier}`} />
            <Stat label="Potential uplift" value={s.uplift_answered > 0 ? `+${s.uplift_answered} (+${s.uplift_pct?.toFixed(0)}%)` : "—"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide opacity-60">{label}</div>
      <div className="font-semibold">{value ?? "—"}</div>
    </div>
  );
}

function InsightsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);
  const [showAll, setShowAll] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/dialer/insights?window_hours=${windowHours}`);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowHours]);

  const insights = data?.insights || [];
  const shown = showAll ? insights : insights.slice(0, 2);
  const windowLabel = windowHours === 24 ? "last 24h" : windowHours === 168 ? "last 7d" : `last ${windowHours}h`;

  return (
    <Card className="p-5 space-y-4" data-testid="insights-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <h2 className="font-display text-lg font-semibold tracking-tight">Daily Insights</h2>
          <Badge variant="outline" className="text-[10px] uppercase">{windowLabel}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(windowHours)} onValueChange={(v) => setWindowHours(Number(v))}>
            <SelectTrigger data-testid="insights-window" className="h-8 w-28 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="24">Last 24h</SelectItem>
              <SelectItem value="72">Last 3d</SelectItem>
              <SelectItem value="168">Last 7d</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="insights-refresh" size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
      ) : insights.length === 0 ? (
        <div className="text-sm text-muted-foreground italic text-center py-4" data-testid="insights-empty">
          No campaign activity in the {windowLabel}. Start a dialer run to see how the engine
          would re-pace it.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map(i => <InsightItem key={i.campaign_id} insight={i} />)}
          {insights.length > 2 && (
            <Button
              data-testid="insights-toggle-all" variant="ghost" size="sm"
              onClick={() => setShowAll(s => !s)}
              className="w-full text-xs"
            >
              {showAll ? "Show top 2 only" : `Show all ${insights.length} insights`}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------
//  Per-campaign card
// -------------------------------------------------------------
function CampaignRunCard({ campaign, onRefresh }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("predictive");
  const [target, setTarget] = useState(3.0);
  const [aht, setAht] = useState(240);
  const [dialLevel, setDialLevel] = useState(1.0);
  const [showSettings, setShowSettings] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  async function loadStatus() {
    try {
      const r = await api.get(`/dialer/campaigns/${campaign.id}/status`);
      setStatus(r.data);
      const runState = r.data?.run?.state;
      const cd = (campaign?.data) || {};
      // Slice 3.5g — when the run is STOPPED, the card should reflect
      // the admin's current campaign-editor saved settings, NOT the
      // values from the LAST run (which would be stale & confusing).
      // When RUNNING/PAUSED, show the live run config (operator might
      // have started with overrides via the Console).
      if (runState === "running" || runState === "paused") {
        if (r.data?.run?.mode)               setMode(r.data.run.mode);
        if (r.data?.run?.target_abandon_pct) setTarget(r.data.run.target_abandon_pct);
        if (r.data?.run?.aht_seconds)        setAht(r.data.run.aht_seconds);
        if (r.data?.run?.dial_level)         setDialLevel(r.data.run.dial_level);
      } else {
        // Stopped → mirror the campaign.data
        if (cd.mode)         setMode(cd.mode);
        if (cd.dial_level)   setDialLevel(Number(cd.dial_level));
        if (cd.abandon_target_pct) setTarget(cd.abandon_target_pct);
      }
    } catch (e) { /* silent on poll */ }
  }
  useEffect(() => {
    loadStatus();
    const i = setInterval(loadStatus, 4000);
    return () => clearInterval(i);
  }, [campaign.id]);

  async function start() {
    setBusy(true);
    try {
      await api.post(`/dialer/campaigns/${campaign.id}/start`, {
        mode, target_abandon_pct: Number(target),
        aht_seconds: Number(aht), max_attempts: 4, retry_gap_minutes: 60,
        dial_level: Number(dialLevel || 1.0),
      });
      toast.success(
        `Dialer started in ${mode} mode${mode === "power" ? ` (1:${Number(dialLevel).toFixed(1)})` : ""}`,
      );
      await loadStatus(); onRefresh?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }
  async function pause() {
    setBusy(true);
    try { await api.post(`/dialer/campaigns/${campaign.id}/pause`); toast.message("Paused"); await loadStatus(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }
  async function resume() {
    setBusy(true);
    try { await api.post(`/dialer/campaigns/${campaign.id}/resume`); toast.success("Resumed"); await loadStatus(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }
  async function stop() {
    setBusy(true);
    try { await api.post(`/dialer/campaigns/${campaign.id}/stop`); toast.message("Stopped"); await loadStatus(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }
  async function tick() {
    setBusy(true);
    try {
      const r = await api.post(`/dialer/campaigns/${campaign.id}/tick`);
      const picked = r.data?.picked?.length || 0;
      toast.success(`Tick fired — ${picked} dial${picked === 1 ? "" : "s"} placed`);
      await loadStatus();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  const run = status?.run || {};
  const lp = status?.list_progress || { pending: 0, retry: 0, done: 0, dnc: 0, total: 0 };
  const stats = run.stats || {};
  const decision = status?.decision || {};
  const state = run.state || "stopped";
  const isRunning = state === "running";
  const isPaused = state === "paused";

  const progressPct = ratio(lp.done + lp.dnc, lp.total || 1);
  // Slice 3.5 — surface the last originate error so operators don't have
  // to read backend logs to know WHY 0 calls dialed despite agents free.
  const lastError = run.last_error;
  const originateErrors = stats.originate_errors || 0;

  return (
    <Card className="p-5 space-y-4" data-testid={`dialer-card-${campaign.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="w-4 h-4 text-primary"/>
            <h3 className="font-display font-semibold tracking-tight truncate" data-testid={`camp-name-${campaign.id}`}>
              {campaign.name}
            </h3>
            <Badge className={`text-[10px] uppercase border ${MODE_BADGE[mode]?.color || "bg-slate-100 text-slate-700"}`} data-testid={`mode-badge-${campaign.id}`}>
              {MODE_BADGE[mode]?.label || mode || "—"}
              {mode === "power" && dialLevel ? ` · 1:${Number(dialLevel).toFixed(1)}` : ""}
            </Badge>
            <Badge className={`text-[10px] uppercase ${STATE_BADGE[state]?.color || ""}`} data-testid={`state-${campaign.id}`}>
              {STATE_BADGE[state]?.label || state}
            </Badge>
          </div>
          {campaign.description && (
            <p className="text-xs text-muted-foreground">{campaign.description}</p>
          )}
          {status?.calling_window?.enabled && (
            <div className="mt-1.5"><CallingWindowPill window={status.calling_window} testId={`camp-window-${campaign.id}`} /></div>
          )}
        </div>
        <div className="flex gap-1.5">
          {!isRunning && !isPaused && (
            <Button data-testid={`start-${campaign.id}`} size="sm" onClick={start} disabled={busy} className="gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5"/>} Start
            </Button>
          )}
          {isRunning && (
            <>
              <Button data-testid={`tick-${campaign.id}`} size="sm" variant="outline" onClick={tick} disabled={busy} className="gap-1.5">
                <Activity className="w-3.5 h-3.5"/> Tick
              </Button>
              <Button data-testid={`pause-${campaign.id}`} size="sm" variant="outline" onClick={pause} disabled={busy} className="gap-1.5">
                <Pause className="w-3.5 h-3.5"/> Pause
              </Button>
            </>
          )}
          {isPaused && (
            <Button data-testid={`resume-${campaign.id}`} size="sm" onClick={resume} disabled={busy} className="gap-1.5">
              <Play className="w-3.5 h-3.5"/> Resume
            </Button>
          )}
          {(isRunning || isPaused) && (
            <Button data-testid={`stop-${campaign.id}`} size="sm" variant="destructive" onClick={stop} disabled={busy} className="gap-1.5">
              <Square className="w-3.5 h-3.5"/> Stop
            </Button>
          )}
          <Button data-testid={`diagnose-${campaign.id}`} size="sm" variant="outline" onClick={() => setDiagOpen(true)} className="gap-1.5">
            <Stethoscope className="w-3.5 h-3.5"/> Diagnose
          </Button>
        </div>
      </div>

      <DiagnoseDialog
        open={diagOpen}
        onOpenChange={setDiagOpen}
        campaign={campaign}
      />

      {/* Settings strip */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Mode</Label>
          <Select value={mode} onValueChange={setMode} disabled={isRunning}>
            <SelectTrigger data-testid={`mode-${campaign.id}`} className="h-8 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="preview">Preview</SelectItem>
              <SelectItem value="progressive">Progressive</SelectItem>
              <SelectItem value="power">Power (1:N)</SelectItem>
              <SelectItem value="predictive">Predictive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === "power" ? (
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Dial level (1:N)</Label>
            <Input data-testid={`dial-level-${campaign.id}`} type="number" step="0.1" min="1.0" max="4.0" value={dialLevel}
              onChange={(e) => setDialLevel(Math.max(1.0, Math.min(4.0, Number(e.target.value) || 1.0)))}
              disabled={isRunning} className="h-8 text-xs"/>
          </div>
        ) : (
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Target abandon %</Label>
            <Input data-testid={`target-${campaign.id}`} type="number" step="0.1" value={target}
              onChange={(e) => setTarget(e.target.value)} disabled={isRunning} className="h-8 text-xs"/>
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">AHT (s)</Label>
          <Input data-testid={`aht-${campaign.id}`} type="number" value={aht}
            onChange={(e) => setAht(e.target.value)} disabled={isRunning} className="h-8 text-xs"/>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3 pt-3 border-t border-border">
        <Kpi label="Free agents" value={status?.available_agents ?? "—"} icon={<Phone className="w-3.5 h-3.5"/>} />
        <Kpi label="Multiplier" value={decision?.multiplier ?? "—"} icon={<Gauge className="w-3.5 h-3.5"/>} />
        <Kpi label="Abandon" value={fmtPct(status?.abandon_pct)} tone={Number(status?.abandon_pct) > Number(target) ? "bad" : "good"} />
        <Kpi label="In flight" value={run.in_flight_calls ?? 0} />
      </div>

      {/* List progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Lead list progress</span>
          <span data-testid={`progress-${campaign.id}`}>
            {lp.done + lp.dnc} of {lp.total} processed · {lp.pending + lp.retry} remaining
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="flex gap-3 text-[11px] text-muted-foreground">
          <span>✓ {lp.done} done</span>
          <span>↻ {lp.retry} retry</span>
          <span className="text-amber-700">⛔ {lp.dnc} DNC</span>
          <span>· {stats.dialed || 0} dialed · {stats.answered || 0} answered · {stats.abandoned || 0} abandoned</span>
        </div>
      </div>

      {decision?.explanation && (
        <p className="text-[11px] text-muted-foreground italic" data-testid={`explain-${campaign.id}`}>
          {decision.explanation}
        </p>
      )}

      {/* Slice 3.5 — Originate error banner. Shows when Asterisk rejects an
          outbound origination (most common: trunk endpoint missing from
          pjsip.conf). Operators previously had to read backend logs. */}
      {lastError && (
        <div
          data-testid={`dialer-error-${campaign.id}`}
          className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700/40 px-3 py-2 text-xs"
        >
          <div className="font-semibold text-red-800 dark:text-red-300">
            ⚠ Originate failed ({originateErrors} attempt{originateErrors === 1 ? "" : "s"})
          </div>
          <div className="text-red-700 dark:text-red-200 mt-0.5 break-words">
            {lastError}
          </div>
          <div className="text-[10px] text-red-600 dark:text-red-300/80 mt-1">
            {/^.*(401|Authentication).*$/i.test(lastError) ? (
              <>
                Fix: Asterisk ARI credentials drift. Run on the host{" "}
                <code>cd /opt/connect7 &amp;&amp; sudo ./deploy/fix-ari-401.sh</code>{" "}
                — this syncs <code>deploy/.env</code> and <code>asterisk/ari.conf</code>
                {" "}to the same password and reloads both containers.
              </>
            ) : /not found|404/i.test(lastError) ? (
              <>
                Fix: The SIP trunk endpoint is missing from Asterisk's PJSIP. Click{" "}
                <b>Admin → Telephony → Regenerate</b> to push trunk configs to{" "}
                <code>/etc/asterisk/pjsip.conf</code>, then reload Asterisk.
              </>
            ) : (
              <>
                Check the Asterisk CLI (<code>docker compose exec asterisk asterisk -rvvv</code>){" "}
                for the channel originate trace and verify the trunk is reachable.
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function Kpi({ label, value, icon, tone }) {
  const toneCls = tone === "bad" ? "text-destructive" : tone === "good" ? "text-emerald-700" : "text-foreground";
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}{label}
      </div>
      <div className={`text-lg font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

// -------------------------------------------------------------
//  Pacing simulator
// -------------------------------------------------------------
function PacingSimulator() {
  const [agents, setAgents] = useState(20);
  const [aht, setAht] = useState(240);
  const [target, setTarget] = useState(3.0);
  const [mode, setMode] = useState("predictive");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      const r = await api.post("/dialer/simulate", {
        mode, available_agents: Number(agents), aht_seconds: Number(aht),
        target_abandon_pct: Number(target), in_flight_calls: 0,
      });
      setResult(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }
  useEffect(() => { run(); /* on mount */ /* eslint-disable-next-line */ }, []);

  const sweepMax = result?.sweep?.length ? Math.max(...result.sweep.map(p => p.projected_abandon_pct)) : 1;
  const decision = result?.decision || {};

  return (
    <Card className="p-5 space-y-4" data-testid="pacing-sim">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">Pacing Simulator</h2>
          <p className="text-xs text-muted-foreground">Estimate dial multiplier & projected abandon — no calls placed.</p>
        </div>
        <Button onClick={run} disabled={busy} size="sm" data-testid="sim-run">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Simulate"}
        </Button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 text-sm">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="sim-mode" className="h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="preview">Preview</SelectItem>
              <SelectItem value="progressive">Progressive</SelectItem>
              <SelectItem value="power">Power (1:N)</SelectItem>
              <SelectItem value="predictive">Predictive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Available agents</Label>
          <Input data-testid="sim-agents" type="number" value={agents} onChange={(e)=>setAgents(e.target.value)} className="h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">AHT (seconds)</Label>
          <Input data-testid="sim-aht" type="number" value={aht} onChange={(e)=>setAht(e.target.value)} className="h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Target abandon %</Label>
          <Input data-testid="sim-target" type="number" step="0.1" value={target} onChange={(e)=>setTarget(e.target.value)} className="h-9"/>
        </div>
      </div>

      {result && (
        <div className="space-y-4 pt-3 border-t border-border">
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Recommended multiplier" value={decision.multiplier ?? "—"} tone="good" />
            <Kpi label="Projected abandon" value={fmtPct(decision.projected_abandon_pct)}
              tone={(decision.projected_abandon_pct > target) ? "bad" : "good"} />
            <Kpi label="Dials this tick" value={decision.to_dial ?? "—"} />
          </div>
          {decision.explanation && (
            <p className="text-xs text-muted-foreground italic">{decision.explanation}</p>
          )}

          {/* Abandon-vs-multiplier sweep chart (CSS bars) */}
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Abandon vs multiplier sweep</Label>
            <div className="flex items-end gap-px h-24 mt-1 px-1 border-l border-b border-border" data-testid="sim-sweep">
              {result.sweep?.map((p) => {
                const h = Math.min(100, (p.projected_abandon_pct / Math.max(target * 3, sweepMax || 1)) * 100);
                const over = p.projected_abandon_pct > target;
                return (
                  <div key={p.multiplier} className="flex-1 group relative" title={`x${p.multiplier} → ${p.projected_abandon_pct.toFixed(1)}%`}>
                    <div className={`w-full ${over ? "bg-destructive/70" : "bg-emerald-500/70"} transition-all`} style={{ height: `${h}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>x1.0</span><span>x2.0</span><span>x3.0</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-500/70"/> under target</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-destructive/70"/> over target</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------
//  Top-level page
// -------------------------------------------------------------
export default function PredictiveDialerPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/resources/campaigns");
      // Only outbound dial modes are managed here — Voice Broadcast has its own
      // dashboard and Blended is inbound-led, so both are excluded from the dialer console.
      const DIALER_MODES = new Set(["preview", "progressive", "power", "predictive"]);
      setCampaigns(
        (r.data || [])
          .filter(c => c.is_active !== false)
          .filter(c => DIALER_MODES.has((c.data?.mode || "predictive").toLowerCase()))
      );
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const activeCount = campaigns.length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="dialer-title">
            Predictive Dialer Console
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Start, pace, and monitor outbound campaigns. The engine recommends a dial multiplier
            using Erlang-C and dampens it with live abandon-rate feedback.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase">
          {activeCount} active campaign{activeCount === 1 ? "" : "s"}
        </Badge>
      </div>

      <InsightsCard />

      <PacingSimulator />

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
      ) : campaigns.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-amber-500"/>
          No active campaigns. Create one in <strong>Outbound → Campaigns</strong> first.
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {campaigns.map(c => <CampaignRunCard key={c.id} campaign={c} onRefresh={load} />)}
        </div>
      )}
    </div>
  );
}


// ─── Diagnose dialog ─────────────────────────────────────────────────
// Calls /api/dialer/campaigns/{id}/debug (admin auth required) and
// renders a structured "why isn't this dialing?" report. Replaces the
// "open the raw JSON URL in browser → Not authenticated" workflow.
function DiagnoseDialog({ open, onOpenChange, campaign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/dialer/campaigns/${campaign.id}/debug`);
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="diagnose-dialog">
        <DialogHeader>
          <DialogTitle>Diagnose · {campaign.name}</DialogTitle>
          <DialogDescription>
            Live snapshot of every gate the dialer evaluates. The verdict at top tells you exactly what's blocking dial-out.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>}
        {!loading && data && (
          <div className="space-y-4 text-sm">
            {/* Verdict */}
            <div className="space-y-1.5">
              {(data.verdict || []).map((v, i) => {
                const ok = v.startsWith("OK");
                return (
                  <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-md border ${ok ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200" : "bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700 text-red-900 dark:text-red-200"}`}>
                    {ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/> : <XCircle className="w-4 h-4 mt-0.5 shrink-0"/>}
                    <span className="font-medium">{v}</span>
                  </div>
                );
              })}
            </div>

            {/* ARI / Telephony link status — answers "is the WebSocket up?" at a glance */}
            {data.telephony && (
              <div className="flex flex-wrap gap-2" data-testid="diagnose-telephony">
                {[
                  { k: "ARI WebSocket", v: data.telephony.asterisk_connected },
                  { k: "Stasis app", v: data.telephony.stasis_app_registered },
                ].map(({ k, v }) => {
                  const down = v === false;
                  const unknown = v === null || v === undefined;
                  return (
                    <span key={k} data-testid={`telephony-${k.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        down ? "bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300"
                        : unknown ? "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300"
                        : "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${down ? "bg-red-500" : unknown ? "bg-amber-500" : "bg-emerald-500"}`}/>
                      {k}: {down ? "DOWN" : unknown ? "unknown" : "connected"}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-3">
                <div className="text-[11px] uppercase text-muted-foreground mb-1">Run</div>
                <div className="text-xs space-y-0.5">
                  <div>State: <span className="font-mono font-bold">{data.run?.state}</span></div>
                  <div>Mode: <span className="font-mono">{data.run?.mode}</span></div>
                  {data.campaign?.stage_dialing && (
                    <div>Staging: <span className="font-mono text-emerald-600 dark:text-emerald-400">on</span> · max wait {data.campaign?.customer_max_wait_seconds ?? 3}s</div>
                  )}
                  <div>In-flight (live): {data.run?.in_flight_live}</div>
                  <div>Last tick: <span className="font-mono text-[10px]">{data.run?.last_tick_at || "never"}</span></div>
                  {data.run?.last_error && <div className="text-red-600">Error: {data.run.last_error}</div>}
                </div>
              </Card>
              <Card className="p-3">
                <div className="text-[11px] uppercase text-muted-foreground mb-1">Lead list</div>
                <div className="text-xs space-y-0.5">
                  <div>Pending: <span className="font-bold">{data.list?.pending}</span></div>
                  <div>Retry: {data.list?.retry}</div>
                  <div>Dialing: {data.list?.dialing}</div>
                  <div>Done: {data.list?.done} · DNC: {data.list?.dnc} · Maxed: {data.list?.max_attempts}</div>
                </div>
              </Card>
            </div>

            {/* Trunk */}
            <Card className="p-3">
              <div className="text-[11px] uppercase text-muted-foreground mb-1">Trunk</div>
              <div className="text-xs space-y-0.5">
                <div>Endpoint: <span className="font-mono">{data.trunk?.endpoint || "—"}</span></div>
                <div>Caller ID: <span className="font-mono">{data.trunk?.caller_id || "—"}</span></div>
                <div>Configured: {(data.trunk?.configured_trunks || []).length}</div>
              </div>
            </Card>

            {/* Agent pool */}
            <div>
              <div className="text-[11px] uppercase text-muted-foreground mb-1 flex items-center justify-between">
                <span>Agent pool · {data.pool_eligible_count} eligible of {(data.agents_report || []).length}</span>
                <span className="text-muted-foreground">parked (fresh): {data.parked_legs_fresh_count ?? 0}</span>
              </div>
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40"><tr>
                    <th className="px-2 py-1.5 text-left">Agent</th>
                    <th className="px-2 py-1.5 text-left">Ext</th>
                    <th className="px-2 py-1.5 text-left">Presence</th>
                    <th className="px-2 py-1.5 text-left">Channel</th>
                    <th className="px-2 py-1.5 text-left">State</th>
                    <th className="px-2 py-1.5 text-left">Parked</th>
                    <th className="px-2 py-1.5 text-left">Active here</th>
                    <th className="px-2 py-1.5 text-left">Blocked by</th>
                  </tr></thead>
                  <tbody>
                    {(data.agents_report || []).map((a) => (
                      <tr key={a.id} className={`border-t ${a.eligible ? "bg-emerald-50/40 dark:bg-emerald-950/20" : ""}`}>
                        <td className="px-2 py-1.5">{a.name}</td>
                        <td className="px-2 py-1.5 font-mono">{a.extension}</td>
                        <td className="px-2 py-1.5">{a.presence}</td>
                        <td className="px-2 py-1.5">{a.channel_mode || "—"}</td>
                        <td className="px-2 py-1.5 font-mono">{a.agent_state || "—"}</td>
                        <td className="px-2 py-1.5">{a.parked ? "✓" : "✗"}</td>
                        <td className="px-2 py-1.5">{a.active_on_this_campaign ? "✓" : "—"}</td>
                        <td className="px-2 py-1.5 text-red-700 dark:text-red-300">{(a.excluded_because || []).join(", ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Slice 3.5l — Park-State Mirror snapshot */}
              {(data.parked_legs || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase text-muted-foreground mb-1">parked_legs (live)</div>
                  <div className="rounded-md border overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/40"><tr>
                        <th className="px-2 py-1.5 text-left">Ext</th>
                        <th className="px-2 py-1.5 text-left">Conf</th>
                        <th className="px-2 py-1.5 text-left">Channel</th>
                        <th className="px-2 py-1.5 text-left">Last seen</th>
                        <th className="px-2 py-1.5 text-left">Fresh</th>
                      </tr></thead>
                      <tbody>
                        {(data.parked_legs || []).map((p, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1.5 font-mono">{p.agent_ext}</td>
                            <td className="px-2 py-1.5 font-mono">{p.conf_name}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{p.channel_id}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{p.last_seen_at || p.joined_at}</td>
                            <td className="px-2 py-1.5">{p.fresh ? "✓" : "stale"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={load} data-testid="diagnose-refresh">
                <Activity className="w-3.5 h-3.5 mr-1"/> Refresh
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
